import { useEffect, useMemo, useState } from 'react';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import { STYLE_PRESETS, StylePresetValue } from './constants/stylePresets';
import {
  requestChatCompletion,
  requestImageGeneration,
  ChatMessagePayload,
  fetchProviderModels,
  ProviderModel,
  requestScenarioBuilderStep,
  ScenarioBuilderResponse
} from './services/api';
import type {
  ChatMessage,
  ScenarioBuilderState,
  SavedScenario,
  ScenarioHistoryEntry,
  ScenarioDraft
} from './types';

type ActiveTab = 'chat' | 'visuals';
type Mode = 'studio' | 'builder';

const LOCAL_SCENARIOS_KEY = 'lyra-roleplay-scenarios';

const INITIAL_BUILDER_STATE: ScenarioBuilderState = {
  status: 'idle',
  history: [],
  currentQuestion: null,
  scenario: null,
  confirmation: null,
  predefinedQuestions: [
    'Who is the protagonist and what do they desperately want right now?',
    'Where does the opening scene unfold? Describe one striking detail that sets the tone.',
    'What obstacle or antagonist immediately complicates the protagonist’s goal?',
    'What emotional energy should guide the first exchange (tender, tense, flirty, etc.)?',
    'What kind of payoff or twist should the scene be steering toward?'
  ],
  tips: []
};

const DEFAULT_PERSONA =
  'Lyra Vance, a quick-witted starship captain who hides her softness behind swagger.';
const DEFAULT_SCENARIO =
  'Neon cantina on the fringe of a lawless asteroid belt moments before a high-stakes heist.';
const DEFAULT_STYLE: StylePresetValue = 'dramatic';

const normalizeStylePreset = (value?: string | null): StylePresetValue | null => {
  if (!value) {
    return null;
  }
  const lowered = value.toLowerCase() as StylePresetValue;
  return STYLE_PRESETS.some((preset) => preset.value === lowered) ? lowered : null;
};

const renderMessage = (message: ChatMessage) => (
  <div key={message.id} className={`message message-${message.role}`}>
    <div className="message-meta">
      <span>{message.role === 'user' ? 'You' : 'Lyra AI'}</span>
      <span>{new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
    </div>
    <div className="message-content">{message.content}</div>
  </div>
);

const toPayload = (messages: ChatMessage[]): ChatMessagePayload[] =>
  messages.map((message) => ({
    role: message.role,
    content: message.content
  }));

const App = () => {
  const { miniApp, initDataRaw, username } = useTelegramWebApp();
  const [mode, setMode] = useState<Mode>('studio');
  const [activeTab, setActiveTab] = useState<ActiveTab>('chat');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [composerDraft, setComposerDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [stylePreset, setStylePreset] = useState<StylePresetValue>(DEFAULT_STYLE);
  const [persona, setPersona] = useState(DEFAULT_PERSONA);
  const [scenario, setScenario] = useState(DEFAULT_SCENARIO);
  const [temperature, setTemperature] = useState(0.9);
  const [savedScenarios, setSavedScenarios] = useState<SavedScenario[]>(() => {
    if (typeof window === 'undefined') {
      return [];
    }
    try {
      const raw = window.localStorage.getItem(LOCAL_SCENARIOS_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as SavedScenario[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [builderState, setBuilderState] = useState<ScenarioBuilderState>(INITIAL_BUILDER_STATE);
  const [builderAnswerDraft, setBuilderAnswerDraft] = useState('');
  const [isBuilderLoading, setIsBuilderLoading] = useState(false);
  const [editingScenarioId, setEditingScenarioId] = useState<string | null>(null);

  const [imagePrompt, setImagePrompt] = useState(
    'Lyra Vance poised on a balcony above a neon city, dual moons glowing overhead'
  );
  const [imageResults, setImageResults] = useState<Array<{ url: string; revisedPrompt?: string | null }>>([]);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [availableModels, setAvailableModels] = useState<ProviderModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [selectedChatModel, setSelectedChatModel] = useState<string | null>(null);
  const [selectedImageModel, setSelectedImageModel] = useState<string | null>(null);
  const [serverDefaults, setServerDefaults] = useState<{ chat: string | null; image: string | null }>({
    chat: null,
    image: null
  });

  const hasTelegramContext = Boolean(initDataRaw);
  const serverChatDefaultAvailable = Boolean(serverDefaults.chat);
  const serverImageDefaultAvailable = Boolean(serverDefaults.image);
  const canSend =
    mode === 'studio' &&
    composerDraft.trim().length > 0 &&
    !isSending &&
    hasTelegramContext &&
    (serverChatDefaultAvailable || (!isLoadingModels && Boolean(selectedChatModel)));

  useEffect(() => {
    if (!hasTelegramContext || !initDataRaw) {
      setAvailableModels([]);
      setModelsError(null);
      setSelectedChatModel(null);
      setSelectedImageModel(null);
      setServerDefaults({ chat: null, image: null });
      return;
    }

    let cancelled = false;
    setIsLoadingModels(true);

    fetchProviderModels(initDataRaw)
      .then(({ models, defaults }) => {
        if (cancelled) {
          return;
        }
        setAvailableModels(models);
        setModelsError(null);
        setServerDefaults({
          chat: defaults.chat ?? null,
          image: defaults.image ?? null
        });

        setSelectedChatModel((previous) => {
          if (previous && models.some((model) => model.id === previous)) {
            return previous;
          }
          if (defaults.chat) {
            return null;
          }
          const fallback =
            models.find((model) => model.type === 'chat') ??
            models.find((model) => model.type === 'other') ??
            null;
          return fallback ? fallback.id : null;
        });

        setSelectedImageModel((previous) => {
          if (previous && models.some((model) => model.id === previous && model.type === 'image')) {
            return previous;
          }
          if (defaults.image) {
            return null;
          }
          const fallback = models.find((model) => model.type === 'image') ?? null;
          return fallback ? fallback.id : null;
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Unable to load models.';
        setModelsError(message);
        setServerDefaults({ chat: null, image: null });
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingModels(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hasTelegramContext, initDataRaw]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(LOCAL_SCENARIOS_KEY, JSON.stringify(savedScenarios));
    } catch {
      // ignore storage errors
    }
  }, [savedScenarios]);

  const chatModels = useMemo(
    () => availableModels.filter((model) => model.type === 'chat' || model.type === 'other'),
    [availableModels]
  );

  const imageModels = useMemo(
    () => availableModels.filter((model) => model.type === 'image'),
    [availableModels]
  );

  const resolveBuilderModel = () =>
    selectedChatModel ?? serverDefaults.chat ?? (chatModels[0]?.id ?? null);

  const currentStylePreset = useMemo(
    () => STYLE_PRESETS.find((preset) => preset.value === stylePreset) ?? null,
    [stylePreset]
  );

  const activeChatModelLabel = useMemo(() => {
    if (!selectedChatModel) {
      return serverChatDefaultAvailable
        ? `Server default${serverDefaults.chat ? ` (${serverDefaults.chat})` : ''}`
        : 'Select a model';
    }
    return availableModels.find((model) => model.id === selectedChatModel)?.displayName ?? selectedChatModel;
  }, [availableModels, selectedChatModel, serverChatDefaultAvailable, serverDefaults.chat]);

  const activeImageModelLabel = useMemo(() => {
    if (!selectedImageModel) {
      return serverImageDefaultAvailable
        ? `Server default${serverDefaults.image ? ` (${serverDefaults.image})` : ''}`
        : 'Select a model';
    }
    return availableModels.find((model) => model.id === selectedImageModel)?.displayName ?? selectedImageModel;
  }, [availableModels, selectedImageModel, serverDefaults.image, serverImageDefaultAvailable]);

  const chatModelStatus = useMemo(() => {
    if (!hasTelegramContext) {
      return 'Unavailable outside Telegram';
    }
    if (isLoadingModels) {
      return 'Loading models...';
    }
    if (modelsError) {
      return 'Models unavailable';
    }
    if (!serverChatDefaultAvailable && !selectedChatModel) {
      return 'Select a model';
    }
    return activeChatModelLabel;
  }, [
    hasTelegramContext,
    isLoadingModels,
    modelsError,
    activeChatModelLabel,
    selectedChatModel,
    serverChatDefaultAvailable
  ]);

  const imageModelStatus = useMemo(() => {
    if (!hasTelegramContext) {
      return 'Unavailable outside Telegram';
    }
    if (isLoadingModels) {
      return 'Loading models...';
    }
    if (modelsError) {
      return 'Models unavailable';
    }
    if (!serverImageDefaultAvailable && !selectedImageModel) {
      return 'Select a model';
    }
    return activeImageModelLabel;
  }, [
    hasTelegramContext,
    isLoadingModels,
    modelsError,
    activeImageModelLabel,
    selectedImageModel,
    serverImageDefaultAvailable
  ]);

  const chatModelHelperText = useMemo(() => {
    if (!hasTelegramContext) {
      return 'Open in Telegram to manage models.';
    }
    if (modelsError) {
      return `Unable to load models (${modelsError}).`;
    }
    if (isLoadingModels && chatModels.length === 0) {
      return 'Fetching provider models...';
    }
    if (!serverChatDefaultAvailable && chatModels.length === 0) {
      return 'No chat models detected. Configure your provider or set AI_CHAT_MODEL on the server.';
    }
    if (serverChatDefaultAvailable) {
      return serverDefaults.chat
        ? `Server default: ${serverDefaults.chat}. Override below when needed.`
        : 'Server default is configured. Override below when needed.';
    }
    return 'Choose a chat model (required).';
  }, [
    hasTelegramContext,
    modelsError,
    isLoadingModels,
    chatModels.length,
    serverChatDefaultAvailable,
    serverDefaults.chat
  ]);

  const imageModelHelperText = useMemo(() => {
    if (!hasTelegramContext) {
      return 'Open in Telegram to manage models.';
    }
    if (modelsError) {
      return `Unable to load models (${modelsError}).`;
    }
    if (isLoadingModels && imageModels.length === 0) {
      return 'Fetching provider models...';
    }
    if (!serverImageDefaultAvailable && imageModels.length === 0) {
      return 'No image models detected. Configure your provider or set AI_IMAGE_MODEL on the server.';
    }
    if (serverImageDefaultAvailable) {
      return serverDefaults.image
        ? `Server default: ${serverDefaults.image}. Override below when you want another renderer.`
        : 'Server default is configured. Override below when you want another renderer.';
    }
    return 'Choose an image model (required).';
  }, [
    hasTelegramContext,
    modelsError,
    isLoadingModels,
    imageModels.length,
    serverDefaults.image,
    serverImageDefaultAvailable
  ]);

  const showAlert = (message: string) => {
    if (miniApp) {
      miniApp.showAlert(message);
    } else {
      window.alert(message);
    }
  };

  const applyBuilderResponse = (
    response: ScenarioBuilderResponse,
    history: ScenarioHistoryEntry[],
    ctx: { currentQuestion: string | null; answer?: string }
  ) => {
    const normalizedScenario: ScenarioDraft = {
      ...response.scenario,
      stylePreset: normalizeStylePreset(response.scenario.stylePreset ?? null)
    };

    if (response.status === 'confirm') {
      setBuilderState({
        status: 'awaitingConfirmation',
        history,
        currentQuestion: ctx.currentQuestion,
        confirmation: {
          prompt: response.confirmation?.prompt ?? 'Does this align with your vision?',
          suggestedEnhancement: response.confirmation?.suggestedEnhancement ?? null,
          answer: ctx.answer ?? ''
        },
        scenario: normalizedScenario,
        predefinedQuestions: response.predefinedQuestions,
        tips: response.tips ?? []
      });
      return;
    }

    if (response.status === 'complete') {
      setBuilderState({
        status: 'complete',
        history,
        currentQuestion: null,
        confirmation: null,
        scenario: normalizedScenario,
        predefinedQuestions: response.predefinedQuestions,
        tips: response.tips ?? []
      });
      return;
    }

    setBuilderState({
      status: 'awaitingAnswer',
      history,
      currentQuestion: response.question ?? ctx.currentQuestion ?? null,
      confirmation: null,
      scenario: normalizedScenario,
      predefinedQuestions: response.predefinedQuestions,
      tips: response.tips ?? []
    });
  };

  const startBuilderSession = async (
    options: { history?: ScenarioHistoryEntry[]; scenarioDraft?: ScenarioDraft | null; editingId?: string | null } = {}
  ) => {
    if (!hasTelegramContext) {
      showAlert('Launch the mini app inside Telegram to use the scenario builder.');
      return;
    }

    const model = resolveBuilderModel();
    if (!model) {
      showAlert('Select a chat model in Roleplay Studio before using the scenario builder.');
      return;
    }

    setIsBuilderLoading(true);
    try {
      const history = options.history ?? [];
      const response = await requestScenarioBuilderStep(
        {
          mode: 'init',
          history,
          scenarioDraft: options.scenarioDraft ?? null,
          model
        },
        initDataRaw
      );

      applyBuilderResponse(response, history, { currentQuestion: response.question ?? null });
      setBuilderAnswerDraft('');
      setEditingScenarioId(options.editingId ?? null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to start the scenario builder.';
      showAlert(message);
    } finally {
      setIsBuilderLoading(false);
    }
  };

  const submitBuilderAnswer = async () => {
    if (!builderState.currentQuestion) {
      showAlert('No active question. Tap "Start builder" to begin.');
      return;
    }

    const trimmedAnswer = builderAnswerDraft.trim();
    if (!trimmedAnswer) {
      showAlert('Add an answer before continuing.');
      return;
    }

    if (!hasTelegramContext) {
      showAlert('Open the mini app inside Telegram to continue the scenario builder.');
      return;
    }

    const model = resolveBuilderModel();
    if (!model) {
      showAlert('Select a chat model in Roleplay Studio before using the scenario builder.');
      return;
    }

    setIsBuilderLoading(true);
    try {
      const response = await requestScenarioBuilderStep(
        {
          mode: 'answer',
          history: builderState.history,
          question: builderState.currentQuestion,
          answer: trimmedAnswer,
          scenarioDraft: builderState.scenario,
          model
        },
        initDataRaw
      );

      setBuilderAnswerDraft(trimmedAnswer);
      applyBuilderResponse(response, builderState.history, {
        currentQuestion: builderState.currentQuestion,
        answer: trimmedAnswer
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to process that answer. Please try again.';
      showAlert(message);
    } finally {
      setIsBuilderLoading(false);
    }
  };

  const confirmBuilderAnswer = async () => {
    if (!builderState.currentQuestion || !builderState.confirmation) {
      return;
    }

    if (!hasTelegramContext) {
      showAlert('Open the mini app inside Telegram to continue the scenario builder.');
      return;
    }

    const model = resolveBuilderModel();
    if (!model) {
      showAlert('Select a chat model in Roleplay Studio before using the scenario builder.');
      return;
    }

    const updatedHistory: ScenarioHistoryEntry[] = [
      ...builderState.history,
      {
        question: builderState.currentQuestion,
        answer: builderState.confirmation.answer
      }
    ];

    setIsBuilderLoading(true);
    try {
      const response = await requestScenarioBuilderStep(
        {
          mode: 'confirm',
          history: updatedHistory,
          question: builderState.currentQuestion,
          answer: builderState.confirmation.answer,
          scenarioDraft: builderState.scenario,
          model
        },
        initDataRaw
      );

      setBuilderAnswerDraft('');
      applyBuilderResponse(response, updatedHistory, { currentQuestion: response.question ?? null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Confirmation failed. Please try again.';
      showAlert(message);
    } finally {
      setIsBuilderLoading(false);
    }
  };

  const finalizeBuilder = async () => {
    if (!hasTelegramContext) {
      showAlert('Open the mini app inside Telegram to continue the scenario builder.');
      return;
    }

    const model = resolveBuilderModel();
    if (!model) {
      showAlert('Select a chat model in Roleplay Studio before using the scenario builder.');
      return;
    }

    setIsBuilderLoading(true);
    try {
      const response = await requestScenarioBuilderStep(
        {
          mode: 'finalize',
          history: builderState.history,
          scenarioDraft: builderState.scenario ?? null,
          model
        },
        initDataRaw
      );

      applyBuilderResponse(response, builderState.history, { currentQuestion: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to finalize the scenario yet.';
      showAlert(message);
    } finally {
      setIsBuilderLoading(false);
    }
  };

  const resetBuilder = () => {
    setBuilderState(INITIAL_BUILDER_STATE);
    setBuilderAnswerDraft('');
    setEditingScenarioId(null);
  };

  const persistScenario = (draft: ScenarioDraft, history: ScenarioHistoryEntry[]) => {
    const timestamp = new Date().toISOString();
    const style = normalizeStylePreset(draft.stylePreset ?? null);
    const updatedDraft: ScenarioDraft = {
      ...draft,
      stylePreset: style
    };

    if (editingScenarioId) {
      setSavedScenarios((prev) =>
        prev.map((scenarioItem) =>
          scenarioItem.id === editingScenarioId
            ? {
                ...scenarioItem,
                ...updatedDraft,
                history,
                updatedAt: timestamp
              }
            : scenarioItem
        )
      );
      setSelectedScenarioId(editingScenarioId);
      return editingScenarioId;
    }

    const newScenario: SavedScenario = {
      ...updatedDraft,
      history,
      id: crypto.randomUUID(),
      createdAt: timestamp,
      updatedAt: timestamp
    };
    setSavedScenarios((prev) => [newScenario, ...prev]);
    setSelectedScenarioId(newScenario.id);
    return newScenario.id;
  };

  const applyScenarioToStudio = (scenarioDraft: SavedScenario) => {
    setPersona(scenarioDraft.persona);
    setScenario(`${scenarioDraft.setting}\n\n${scenarioDraft.synopsis}`);
    if (scenarioDraft.stylePreset) {
      setStylePreset(scenarioDraft.stylePreset);
    }
    setSelectedScenarioId(scenarioDraft.id);
    setMode('studio');
    showAlert('Scenario loaded. Craft your opening line!');
  };

  const deleteScenario = (id: string) => {
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Remove this scenario from your library? This cannot be undone.');
      if (!confirmed) {
        return;
      }
    }

    setSavedScenarios((prev) => prev.filter((item) => item.id !== id));
    if (selectedScenarioId === id) {
      setSelectedScenarioId(null);
    }
    if (editingScenarioId === id) {
      setEditingScenarioId(null);
    }
  };

  const editScenarioInBuilder = (scenarioEntry: SavedScenario) => {
    const { history, id, createdAt, updatedAt, ...draft } = scenarioEntry;
    resetBuilder();
    setMode('builder');
    void startBuilderSession({
      history,
      scenarioDraft: draft,
      editingId: id
    });
  };

  const reviseBuilderAnswer = () => {
    if (!builderState.confirmation) {
      return;
    }
    const fallback =
      builderState.confirmation.suggestedEnhancement ?? builderState.confirmation.answer ?? '';
    setBuilderAnswerDraft(fallback);
    setBuilderState((prev) => ({
      ...prev,
      status: 'awaitingAnswer',
      confirmation: null
    }));
  };

  const handleSaveCurrentScenario = () => {
    if (!builderState.scenario) {
      showAlert('Answer a few more questions so the scenario is ready to save.');
      return;
    }

    const scenarioId = persistScenario(builderState.scenario, builderState.history);
    const normalizedStyle = normalizeStylePreset(builderState.scenario.stylePreset ?? null);
    if (normalizedStyle) {
      setStylePreset(normalizedStyle);
    }
    setPersona(builderState.scenario.persona);
    setScenario(`${builderState.scenario.setting}\n\n${builderState.scenario.synopsis}`);
    setMode('studio');
    setSelectedScenarioId(scenarioId);
    showAlert('Scenario saved to your library. Switch back to chat to play it out!');
  };

  const renderScenarioLibrary = () => (
    <section className="scenario-library">
      <div className="scenario-library-header">
        <h2>Scenario Library</h2>
        <div className="scenario-library-actions">
          <button
            type="button"
            onClick={() => {
              resetBuilder();
              setMode('builder');
              void startBuilderSession();
            }}
          >
            New Scenario
          </button>
        </div>
      </div>
      {savedScenarios.length === 0 ? (
        <p className="panel-hint">No saved scenarios yet. Build one in the Scenario Builder to reuse later.</p>
      ) : (
        <ul className="scenario-list-grid">
          {savedScenarios.map((item) => (
            <li
              key={item.id}
              className={`scenario-card${selectedScenarioId === item.id ? ' active' : ''}`}
            >
              <div className="scenario-card-body">
                <h3>{item.title}</h3>
                <p>{item.synopsis}</p>
                <div className="scenario-card-meta">
                  <span>{item.tone}</span>
                  {item.stylePreset && <span>{item.stylePreset}</span>}
                  <span>{new Date(item.updatedAt).toLocaleString()}</span>
                </div>
              </div>
                <div className="scenario-card-actions">
                  <button type="button" onClick={() => applyScenarioToStudio(item)}>
                    Use in chat
                  </button>
                  <button type="button" onClick={() => editScenarioInBuilder(item)} disabled={isBuilderLoading}>
                    Edit in builder
                  </button>
                  <button type="button" onClick={() => deleteScenario(item.id)}>
                    Delete
                  </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  const handleSendMessage = async () => {
    if (mode !== 'studio') {
      return;
    }

    if (!composerDraft.trim()) {
      return;
    }

    if (!serverChatDefaultAvailable && !selectedChatModel) {
      showAlert('Select a chat model before sending the next line.');
      return;
    }

    const outgoing: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: composerDraft.trim(),
      timestamp: Date.now()
    };

    const payloadMessages = toPayload([...messages, outgoing]);

    setMessages((prev) => [...prev, outgoing]);
    setComposerDraft('');
    setIsSending(true);

    try {
      const response = await requestChatCompletion(
        {
          messages: payloadMessages,
          persona,
          scenario,
          stylePreset,
          temperature,
          model: selectedChatModel
        },
        initDataRaw
      );

      const incoming: ChatMessage = {
        id: crypto.randomUUID(),
        role: response.message.role === 'assistant' ? 'assistant' : 'assistant',
        content: response.message.content,
        timestamp: Date.now()
      };

      setMessages((prev) => [...prev, incoming]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to reach the AI right now.';
      showAlert(message);
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  const handleGenerateImage = async () => {
    if (mode !== 'studio') {
      return;
    }

    if (!imagePrompt.trim() || !hasTelegramContext) {
      return;
    }

    if (!serverImageDefaultAvailable && !selectedImageModel) {
      showAlert('Select an image model before generating visuals.');
      return;
    }

    setIsGeneratingImage(true);
    try {
      const response = await requestImageGeneration(
        {
          prompt: imagePrompt,
          persona,
          scenario,
          stylePreset,
          format: 'b64_json',
          size: '1024x1024',
          quality: 'standard',
          model: selectedImageModel
        },
        initDataRaw
      );

      const nextImages = response.images
        .map((image) => {
          if (image.b64Json) {
            return {
              url: `data:image/png;base64,${image.b64Json}`,
              revisedPrompt: image.revisedPrompt
            };
          }
          if (image.url) {
            return {
              url: image.url,
              revisedPrompt: image.revisedPrompt
            };
          }
          return null;
        })
        .filter((item): item is { url: string; revisedPrompt?: string | null } => Boolean(item));

      setImageResults(nextImages);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Image generation failed.';
      showAlert(message);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleStylePresetChange = (preset: StylePresetValue) => {
    setStylePreset(preset);
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div>
          <h1>Lyra Roleplay Studio</h1>
          <p>
            {username ? `Signed in as ${username}` : 'Launch via Telegram to unlock full features.'} | Mode:{' '}
            {mode === 'studio' ? 'Roleplay Studio' : 'Scenario Builder'}
            {mode === 'studio' ? (
              <>
                {' '}
                | Style: {currentStylePreset?.label ?? 'Custom style'} | Chat model: {chatModelStatus}
              </>
            ) : (
              <>
                {' '}
                | Steps answered: {builderState.history.length}
              </>
            )}
          </p>
        </div>
        <div className="mode-toggle">
          <button
            type="button"
            className={mode === 'studio' ? 'active' : ''}
            onClick={() => setMode('studio')}
          >
            Roleplay Studio
          </button>
          <button
            type="button"
            className={mode === 'builder' ? 'active' : ''}
            onClick={() => setMode('builder')}
          >
            Scenario Builder
          </button>
        </div>
        {mode === 'studio' && (
          <div className="tab-toggle">
            <button
              type="button"
              className={activeTab === 'chat' ? 'active' : ''}
              onClick={() => setActiveTab('chat')}
            >
              Chat
            </button>
            <button
              type="button"
              className={activeTab === 'visuals' ? 'active' : ''}
              onClick={() => setActiveTab('visuals')}
            >
              Visuals
            </button>
          </div>
        )}
      </header>

      {mode === 'studio' ? (
        <>
          <section className="session-controls">
            <label>
              Persona
              <textarea value={persona} onChange={(event) => setPersona(event.target.value)} rows={3} />
            </label>
            <label>
              Scenario
              <textarea value={scenario} onChange={(event) => setScenario(event.target.value)} rows={2} />
            </label>
            <label>
              Style preset
              <select
                value={stylePreset}
                onChange={(event) => handleStylePresetChange(event.target.value as StylePresetValue)}
              >
                {STYLE_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
              {currentStylePreset && <span className="preset-description">{currentStylePreset.description}</span>}
            </label>
            <label>
              Chat model
              <select
                value={selectedChatModel ?? ''}
                onChange={(event) => setSelectedChatModel(event.target.value || null)}
                disabled={
                  !hasTelegramContext ||
                  (isLoadingModels && chatModels.length === 0) ||
                  (!serverChatDefaultAvailable && chatModels.length === 0)
                }
              >
                {serverChatDefaultAvailable && (
                  <option value="">
                    Server default{serverDefaults.chat ? ` (${serverDefaults.chat})` : ''}
                  </option>
                )}
                {chatModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.displayName}
                  </option>
                ))}
              </select>
              <span className="preset-description">{chatModelHelperText}</span>
            </label>
            <label>
              Image model
              <select
                value={selectedImageModel ?? ''}
                onChange={(event) => setSelectedImageModel(event.target.value || null)}
                disabled={
                  !hasTelegramContext ||
                  (isLoadingModels && imageModels.length === 0) ||
                  (!serverImageDefaultAvailable && imageModels.length === 0)
                }
              >
                {serverImageDefaultAvailable && (
                  <option value="">
                    Server default{serverDefaults.image ? ` (${serverDefaults.image})` : ''}
                  </option>
                )}
                {imageModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.displayName}
                  </option>
                ))}
              </select>
              <span className="preset-description">{imageModelHelperText}</span>
            </label>
            <label>
              Temperature ({temperature.toFixed(1)})
              <input
                type="range"
                min={0}
                max={2}
                step={0.1}
                value={temperature}
                onChange={(event) => setTemperature(parseFloat(event.target.value))}
              />
            </label>
          </section>

          {renderScenarioLibrary()}

          {activeTab === 'chat' ? (
            <section className="chat-panel">
              <div className="message-list">
                {!hasTelegramContext ? (
                  <div className="message-empty">
                    <p>Open this mini app inside Telegram to receive AI replies.</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="message-empty">
                    <p>Set the vibe above and send the first line to spark the scene.</p>
                  </div>
                ) : (
                  messages.map(renderMessage)
                )}
              </div>
              <div className="composer">
                <textarea
                  rows={3}
                  placeholder="Write your next move..."
                  value={composerDraft}
                  onChange={(event) => setComposerDraft(event.target.value)}
                  disabled={isSending || !hasTelegramContext}
                />
                <button type="button" onClick={handleSendMessage} disabled={!canSend}>
                  {isSending ? 'Responding...' : 'Send'}
                </button>
              </div>
            </section>
          ) : (
            <section className="image-panel">
              {!hasTelegramContext && (
                <p className="panel-hint">Open this experience inside Telegram to unlock AI visuals.</p>
              )}
              <p className="panel-hint">Image model: {imageModelStatus}</p>
              <textarea
                rows={4}
                value={imagePrompt}
                onChange={(event) => setImagePrompt(event.target.value)}
                placeholder="Describe the moment you want to capture..."
              />
              <button
                type="button"
                onClick={handleGenerateImage}
                disabled={
                  isGeneratingImage ||
                  !hasTelegramContext ||
                  (!serverImageDefaultAvailable && !selectedImageModel)
                }
              >
                {isGeneratingImage ? 'Summoning visuals...' : 'Generate image'}
              </button>

              {imageResults.length > 0 && (
                <div className="image-grid">
                  {imageResults.map((image, index) => (
                    <figure key={index}>
                      <img src={image.url} alt={`Generated scene ${index + 1}`} />
                      {image.revisedPrompt && <figcaption>{image.revisedPrompt}</figcaption>}
                    </figure>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      ) : (
        <>
          <section className="builder-layout">
            <div className="builder-column builder-interaction">
              <h2>Scenario Builder</h2>
              {editingScenarioId && <span className="builder-tag">Editing saved scenario</span>}
              <p>
                Answer Lyra&apos;s prompts to shape a bespoke scenario. Each reply gets a confirmation so you
                can enhance or keep it.
              </p>
              {isBuilderLoading && <p className="builder-status">Coordinating with the AI...</p>}
              <div className="builder-buttons">
                <button
                  type="button"
                  onClick={() => {
                    resetBuilder();
                    void startBuilderSession();
                  }}
                  disabled={isBuilderLoading}
                >
                  {builderState.status === 'idle' ? 'Start builder' : 'Restart builder'}
                </button>
                <button
                  type="button"
                  onClick={() => void finalizeBuilder()}
                  disabled={isBuilderLoading || builderState.history.length === 0}
                >
                  Summarize now
                </button>
                <button type="button" onClick={resetBuilder} disabled={isBuilderLoading}>
                  Clear builder
                </button>
              </div>
              <div className="builder-question-bank">
                <h3>Sample prompts</h3>
                <ul>
                  {builderState.predefinedQuestions.map((prompt, index) => (
                    <li key={index}>{prompt}</li>
                  ))}
                </ul>
              </div>

              {builderState.status === 'idle' && (
                <p className="panel-hint">
                  Tap &ldquo;Start builder&rdquo; to ask the first question. You can also pick a saved scenario
                  below to edit it.
                </p>
              )}

              {builderState.status === 'awaitingAnswer' && builderState.currentQuestion && (
                <div className="builder-card">
                  <h3>Current question</h3>
                  <p>{builderState.currentQuestion}</p>
                  <textarea
                    rows={4}
                    value={builderAnswerDraft}
                    onChange={(event) => setBuilderAnswerDraft(event.target.value)}
                    disabled={isBuilderLoading}
                    placeholder="Your answer..."
                  />
                  <div className="builder-actions">
                    <button type="button" onClick={submitBuilderAnswer} disabled={isBuilderLoading}>
                      {isBuilderLoading ? 'Sending...' : 'Submit answer'}
                    </button>
                  </div>
                </div>
              )}

              {builderState.status === 'awaitingAnswer' && !builderState.currentQuestion && (
                <p className="panel-hint">Tap "Summarize now" or restart to generate the next prompt.</p>
              )}

              {builderState.status === 'awaitingConfirmation' && builderState.confirmation && (
                <div className="builder-card">
                  <h3>Review this beat</h3>
                  <p>{builderState.confirmation.prompt}</p>
                  <p className="builder-answer-review">
                    <strong>Your answer:</strong> {builderState.confirmation.answer}
                  </p>
                  {builderState.confirmation.suggestedEnhancement && (
                    <blockquote>{builderState.confirmation.suggestedEnhancement}</blockquote>
                  )}
                  <div className="builder-actions">
                    <button type="button" onClick={confirmBuilderAnswer} disabled={isBuilderLoading}>
                      Keep answer
                    </button>
                    <button type="button" onClick={reviseBuilderAnswer} disabled={isBuilderLoading}>
                      Revise answer
                    </button>
                  </div>
                </div>
              )}

              {builderState.status === 'complete' && builderState.scenario && (
                <div className="builder-card">
                  <h3>Scenario ready</h3>
                  <p>Save it to your library or ask for another angle.</p>
                  <div className="builder-actions">
                    <button type="button" onClick={handleSaveCurrentScenario} disabled={isBuilderLoading}>
                      Save & switch to chat
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        void startBuilderSession({
                          history: builderState.history,
                          scenarioDraft: builderState.scenario ?? null,
                          editingId: editingScenarioId
                        })
                      }
                      disabled={isBuilderLoading}
                    >
                      Ask another question
                    </button>
                  </div>
                </div>
              )}

              {builderState.history.length > 0 && (
                <div className="builder-card builder-history">
                  <h3>Scenario notes</h3>
                  <ul>
                    {builderState.history.map((entry, index) => (
                      <li key={`${entry.question}-${index}`}>
                        <strong>Q{index + 1}:</strong> {entry.question}
                        <br />
                        <span className="builder-answer-label">A:</span> {entry.answer}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {builderState.tips.length > 0 && (
                <div className="builder-card builder-tips">
                  <h3>AI tips</h3>
                  <ul>
                    {builderState.tips.map((tip, index) => (
                      <li key={index}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="builder-column builder-summary">
              <h2>Scenario draft</h2>
              {builderState.scenario ? (
                <div className="builder-summary-card">
                  <h3>{builderState.scenario.title}</h3>
                  <p>
                    <strong>Persona:</strong> {builderState.scenario.persona}
                  </p>
                  <p>
                    <strong>Setting:</strong> {builderState.scenario.setting}
                  </p>
                  <p>
                    <strong>Tone:</strong> {builderState.scenario.tone}
                  </p>
                  <p>
                    <strong>Synopsis:</strong> {builderState.scenario.synopsis}
                  </p>
                  {builderState.scenario.objectives && builderState.scenario.objectives.length > 0 && (
                    <div>
                      <strong>Objectives:</strong>
                      <ul>
                        {builderState.scenario.objectives.map((objective, index) => (
                          <li key={index}>{objective}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {builderState.scenario.supportingCharacters &&
                    builderState.scenario.supportingCharacters.length > 0 && (
                      <div>
                        <strong>Supporting cast:</strong>
                        <ul>
                          {builderState.scenario.supportingCharacters.map((character, index) => (
                            <li key={index}>{character}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {builderState.scenario.tags && builderState.scenario.tags.length > 0 && (
                    <div className="builder-tags">
                      {builderState.scenario.tags.map((tag, index) => (
                        <span key={index}>{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="panel-hint">Answer the first question to see the outline take shape here.</p>
              )}
            </div>
          </section>

          {renderScenarioLibrary()}
        </>
      )}
    </div>
  );
};

export default App;







